import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { BACKEND_API_URL, ONBOARDING_MAX_STEPS } from "../../config";
  
const initialState = {
    currentStep:  1,
    visitedSteps: [1],
    userId: null,
    maxSteps: ONBOARDING_MAX_STEPS,
    token: null,
    navigationDirection: 'forward',
    skippedSteps: [],
    // Answers the hiring manager gave while generating this link.
    // null = not provided by the API, the form then behaves as it did before.
    linkMeta: {
        newType: null,   // 'New Satellite' | 'New Branch' | 'New Rize for Realtor Team' | ...
        isDBA: null,     // 'Yes' | 'No'
    },
}


/**
 * Fallback for the link answers when /api/entrypoint does not return them yet —
 * the margin_management screen already exposes `newType`.
 */
export const fetchLinkMeta = createAsyncThunk(
    'navigator/fetchLinkMeta',
    async (_, { getState, rejectWithValue }) => {
        const token = getState().navigator.token;
        try {
            const response = await fetch(`${BACKEND_API_URL}/api/onboarding/margin_management?token=${token}`, {
                // This is an optional metadata fallback. Its absence is valid.
                errorToast: false,
            });
            if (!response.ok) {
                return rejectWithValue('Link details not available');
            }
            const data = await response.json();
            return { newType: data?.newType ?? null, isDBA: data?.isDBA ?? null };
        } catch (error) {
            return rejectWithValue('Link details not available');
        }
    }
);


export const fetchEntrypoint = createAsyncThunk(
    'navigator/fetchEntrypoint',
    async (token, { rejectWithValue }) => {
      try {
        const response = await fetch(`${BACKEND_API_URL}/api/entrypoint?token=${token}`);

        if (!response.ok) {
          // If the response is not 2xx, it is considered an error
          const errors = await response.json();
          // console.log(errors);
          return rejectWithValue(errors);
        }

        const response_data =  await response.json();
        // Handle the response data
        // console.log('Form submitted successfully', response_data);

        return response_data;
      } catch (error) {
        // Handle submission errors
        console.error('Entrypoint error', error);
        // Throw an error for the rejected action
        return rejectWithValue(error);
      }
    }
  );


  export const SubmitFinalData = createAsyncThunk(
    'FinalData/submit',
    async (data, { getState, dispatch, rejectWithValue }) => {
        try {
          const {token} = getState().navigator;

          const response = await fetch(`${BACKEND_API_URL}/api/final-onboarding-data?token=${token}`, {
              method: 'POST',
              body: JSON.stringify(data),
              headers: {
                  'Content-Type': 'application/json',
              }
          });
          const response_data =  await response.json();
          // Handle the response data
          // console.log('Form submitted successfully', response_data);
    
          return response_data;
        } catch (error) {
          // Handle submission errors
          console.error('Form submission error', error);
          // Throw an error for the rejected action
          return error;
        }
      }
)



export const navigatorSlice = createSlice({
    name: 'navigator',
    initialState,
    reducers: {
        nextStep: (state) => {
          state.navigationDirection = 'forward'; // Set direction to forward
          if (state.currentStep === 3) {
            // Skip reserved Step 4 -> Processing Plan (Step 5)
            state.currentStep = 5;
          } else if (state.currentStep < state.maxSteps) {
            state.currentStep += 1;
          }
          if (!state.visitedSteps.includes(state.currentStep)) {
            state.visitedSteps.push(state.currentStep);
          }
        },
        previousStep: (state) => {
          // state.currentStep -= 1;
          // if (state.visitedSteps.length <= 2) {
          //   state.currentStep -= 1;
          // } else {
          //   state.currentStep = state.visitedSteps[state.visitedSteps.length - 1];
          //   state.visitedSteps.pop();
          // }
          state.navigationDirection = 'backward'; // Set direction to backward
          // Find the previous unskipped step
          const findPreviousStep = (currentStep) => {
              let previousStep = currentStep - 1;
              while (state.skippedSteps.includes(previousStep) && previousStep > 1) {
                  previousStep--;
              }
              return previousStep >= 1 ? previousStep : 1; // Stay within bounds
          };

          const previousStep = findPreviousStep(state.currentStep);

          if (state.currentStep === 5) {
            // Skip reserved Step 4 -> back to License States (Step 3)
            state.currentStep = 3;
          } else {
            state.currentStep = previousStep;
          }
          
          if (!state.visitedSteps.includes(state.currentStep)) {
            state.visitedSteps.push(state.currentStep);
          }
        },
        jumpToStep: (state, action) => {
            const { step } = action.payload;
            state.currentStep = step;
            if (!state.visitedSteps.includes(step)) {
                state.visitedSteps.push(step);
            }
        },
        skipSteps: (state, action) => {
            // Add steps to the `skippedSteps` array
            const stepsToSkip = action.payload;
            state.skippedSteps = [...new Set([...state.skippedSteps, ...stepsToSkip])];
        },
        // user id will be response of first API call, so update it
        updateUserId: (state, action) => {
            const { userId } = action.payload;
            // console.log('user id: ', userId);
            state.userId = userId;
        },
        resetNavigator: (state) => {
            state.currentStep = 1;
            state.visitedSteps = [1];
            state.userId = null;
            state.navigationDirection = 'forward';
            state.skippedSteps = [];
            state.linkMeta = { newType: null, isDBA: null };
        }
    },
    extraReducers: (builder) => {
        builder
          .addCase(fetchEntrypoint.pending, (state) => {
                state.loading = true;
                state.error = null;
          })
          .addCase(fetchEntrypoint.fulfilled, (state, action) => {
            state.loading = false;
            state.currentStep = action.payload.current_step;
            if (!state.visitedSteps.includes(action.payload.current_step)) {
              state.visitedSteps.push(action.payload.current_step);
            }
            const queryParameters = new URLSearchParams(window.location.search)
            state.token = queryParameters.get('token')
            state.linkMeta = {
              newType: action.payload.newType ?? null,
              isDBA: action.payload.isDBA ?? null,
            };
          })
          .addCase(fetchLinkMeta.fulfilled, (state, action) => {
            // only fill in what the entrypoint did not already provide
            state.linkMeta = {
              newType: state.linkMeta.newType ?? action.payload.newType,
              isDBA: state.linkMeta.isDBA ?? action.payload.isDBA,
            };
          })
          .addCase(fetchEntrypoint.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload.error;
          })
          .addCase(SubmitFinalData.pending, (state) => {
            state.loading = true;
            state.error = null;
          })
          .addCase(SubmitFinalData.fulfilled, (state, action) => {
            state.loading = false;
          })
          .addCase(SubmitFinalData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload.error;
          });
      }
});


export const { nextStep, previousStep, resetNavigator, jumpToStep, skipSteps, updateUserId, resetUserId} = navigatorSlice.actions;

export default navigatorSlice.reducer;
